/**
 * Copyright © Binary Anvil, Inc. All rights reserved.
 * See https://binaryanvil.com/software/license for license details.
 */
import React, {ReactNode} from "react";
import Header from "@/app/components/Header/Header";
import { Container } from "@mui/material";
import HeadBlock, {THeadBlockProps} from "@/app/components/layouts/HeadBlock";
import {useAppSelector} from "@/app/hooks/redux";
import {MainNavProvider, useMainNavContext} from "@/app/context/MainNavProvider";
import {getCookie} from "@/app/utils/cookie";
import {SHOW_HEADER_BANNER_COOKIE} from "@/app/constants";
import Footer from '@/app/components/Footer/Footer';

const LayoutFullWidthContent = (props: {
    children:ReactNode,
    HeadBlockProps: THeadBlockProps,
    rootClassName?:string,
    beforeContent?:any,
    afterContent?:any,
    mainContainerClassName?:any,
    ContentWrapperComponent:any
    contentWrapperComponentProps?:any,
}) => {
    const {
        children,
        rootClassName,
        HeadBlockProps,
        beforeContent,
        afterContent,
        mainContainerClassName,
        ContentWrapperComponent,
        contentWrapperComponentProps,
    } = props;
    const {isMenuOpen:isMenuOpenMobile} = useAppSelector(state => state.mobileMenuReducer);
    const {isMainNavOpen} = useMainNavContext();
    const rootWrapperClasses = `root-wrapper ${rootClassName ?? ''} ${isMenuOpenMobile ? 'nav-open-mobile' : ''} ${isMainNavOpen ? 'nav-open-desktop' : ''} ${getCookie({name: SHOW_HEADER_BANNER_COOKIE}) === undefined ? 'with-header-ad-banner' : ''}`.trim();
    return (
        <div className={rootWrapperClasses}>
            <HeadBlock {...HeadBlockProps} />

            <Header/>

            {beforeContent && beforeContent}

            <Container className={`main-content ${mainContainerClassName ? mainContainerClassName : ''}`} component={"main"}>
                <ContentWrapperComponent {...contentWrapperComponentProps} >
                    {children}
                </ContentWrapperComponent>
            </Container>

            {afterContent && afterContent}

            <Footer/>
        </div>
    );
}

const LayoutFullWidth = (props: {
    children:ReactNode,
    headAdditionalNodes?:ReactNode,
    beforeContent?:any,
    afterContent?:any,
    mainContainerClassName?:any,
    rootClassName?:string,
    headTitle:string,
    metaDescription?:string,
    robotsContent?:string,
    metaKeyword?:string,
    canonical?:string,
    ogImgUrl?:string,
    contentWrapperComponent?:any,
    contentWrapperComponentProps?:any,
}) => {
    const {
        children,
        headAdditionalNodes,
        beforeContent,
        afterContent,
        mainContainerClassName,
        rootClassName,
        headTitle,
        metaDescription,
        robotsContent,
        metaKeyword,
        ogImgUrl,
        contentWrapperComponent
    } = props;
    const HeadBlockProps: THeadBlockProps = {
        headTitle
    }

    if (metaKeyword) {
        Object.assign(HeadBlockProps, {metaKeyword})
    }

    if (metaDescription) {
        Object.assign(HeadBlockProps, {metaDescription})
    }

    if (robotsContent) {
        Object.assign(HeadBlockProps, {robotsContent})
    }

    if (ogImgUrl) {
        Object.assign(HeadBlockProps, {ogImgUrl})
    }

    if(headAdditionalNodes) {
        Object.assign(HeadBlockProps, {headAdditionalNodes})
    }

    if(props.canonical) {
        Object.assign(HeadBlockProps, {canonical:props.canonical})
    }

    const ContentWrapperComponent = props.contentWrapperComponent ? props.contentWrapperComponent : React.Fragment;

    return (
        <MainNavProvider>
            <LayoutFullWidthContent
                rootClassName={rootClassName}
                HeadBlockProps={HeadBlockProps}
                beforeContent={beforeContent}
                afterContent={afterContent}
                mainContainerClassName={mainContainerClassName}
                ContentWrapperComponent={ContentWrapperComponent}
                contentWrapperComponentProps={contentWrapperComponent}
            >
                {children}
            </LayoutFullWidthContent>
        </MainNavProvider>
    )
}

export default LayoutFullWidth;